/** Canonical focused PDP routes — no Similar / More / Explore listing sections. */
import { getPdpModeFromPathname, isPremiumPdpMode as isPremiumRouteMode } from './pdpRoutes';

export function isFocusedProjectRoute(pathname = '') {
  return isPremiumRouteMode(getPdpModeFromPathname(pathname));
}

const PREMIUM_SLUGS_KEY = 'proppins:premium-project-slugs';

function normalizeSlug(slug) {
  return String(slug || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-');
}

export function getKnownPremiumProjectSlugs() {
  if (typeof window === 'undefined') return new Set();
  try {
    const raw = sessionStorage.getItem(PREMIUM_SLUGS_KEY);
    const list = JSON.parse(raw || '[]');
    return new Set(
      (Array.isArray(list) ? list : [])
        .map(normalizeSlug)
        .filter(Boolean),
    );
  } catch {
    return new Set();
  }
}

export function syncPremiumProjectSlugs(slugs = []) {
  if (typeof window === 'undefined') return;
  try {
    const set = getKnownPremiumProjectSlugs();
    slugs.forEach((s) => {
      const n = normalizeSlug(s);
      if (n) set.add(n);
    });
    sessionStorage.setItem(PREMIUM_SLUGS_KEY, JSON.stringify([...set]));
  } catch {
    /* ignore */
  }
}

export function rememberPremiumProjectSlug(slug) {
  syncPremiumProjectSlugs([slug]);
}

export function isKnownPremiumSlug(slug) {
  const n = normalizeSlug(slug);
  if (!n) return false;
  return getKnownPremiumProjectSlugs().has(n);
}

function premiumFromSearchParams(searchParams) {
  const qs =
    searchParams instanceof URLSearchParams
      ? searchParams
      : searchParams
        ? new URLSearchParams(String(searchParams))
        : null;
  if (!qs) return false;
  const flag = (qs.get('premium') || '').trim().toLowerCase();
  return flag === '1' || flag === 'true' || flag === 'yes';
}

function isPremiumFromApi(data) {
  if (!data || typeof data !== 'object') return false;

  const candidates = [
    data.is_premium,
    data.isPremium,
    data.premium,
    data.is_featured_premium,
    data.featured_premium,
    data.raw?.is_premium,
    data.raw?.isPremium,
    data.project?.is_premium,
    data.project?.isPremium,
    data.project?.premium,
    data.project?.is_featured_premium,
    data.apartment?.is_premium,
    data.villa?.is_premium,
    data.plot?.is_premium,
    data.property?.is_premium,
    data.details?.is_premium,
    data.project?.details?.is_premium,
    data.apartment_buy_detail?.is_premium,
    data.villa_buy_detail?.is_premium,
    data.plot_buy_detail?.is_premium,
    data.project_detail?.is_premium,
  ];

  return candidates.some((v) => {
    if (v === true || v === 1) return true;
    if (typeof v === 'string') {
      const s = v.trim().toLowerCase();
      return s === 'true' || s === '1' || s === 'yes';
    }
    return false;
  });
}

function premiumFromRouteState(routeState) {
  if (!routeState || typeof routeState !== 'object') return false;
  if (routeState.premiumProject === true) return true;
  if (routeState.is_premium === true || routeState.isPremium === true) return true;
  return false;
}

/** Listing payloads include is_premium; project detail API often omits it. */
export function isPremiumFromRemainingData(slug, remainingData) {
  const slugNorm = normalizeSlug(slug);
  if (!slugNorm || !remainingData) return false;

  const sections = remainingData?.projectSections || remainingData;
  if (sections && typeof sections === 'object') {
    for (const items of Object.values(sections)) {
      if (!Array.isArray(items)) continue;
      const match = items.find(
        (item) => normalizeSlug(item?.slug || item?.project_slug || item?.id) === slugNorm,
      );
      if (match && isPremiumFromApi(match)) return true;
    }
  }

  const safeProps = remainingData?.safeProps;
  if (safeProps && typeof safeProps === 'object') {
    const premiumLists = [
      safeProps.locationpremiumbanner,
      safeProps.locationPremiumBanner,
      safeProps.location_premium_banner,
    ];
    for (const list of premiumLists) {
      if (!Array.isArray(list)) continue;
      if (
        list.some(
          (item) => normalizeSlug(item?.slug || item?.project_slug || item?.id) === slugNorm,
        )
      ) {
        return true;
      }
    }
  }

  return false;
}

export function syncPremiumSlugsFromRemainingData(remainingData) {
  if (!remainingData || typeof remainingData !== 'object') return;

  syncPremiumProjectSlugsFromSearchPayload(remainingData?.safeProps);

  const sections = remainingData?.projectSections || remainingData;
  if (!sections || typeof sections !== 'object') return;

  const slugs = [];
  Object.values(sections).forEach((items) => {
    if (!Array.isArray(items)) return;
    items.forEach((item) => {
      if (isPremiumFromApi(item)) {
        slugs.push(item?.slug || item?.project_slug || item?.id);
      }
    });
  });
  if (slugs.length) syncPremiumProjectSlugs(slugs);
}

export function syncPremiumSlugsFromProjectRelations(projectData) {
  if (!projectData || typeof projectData !== 'object') return;

  const rawLists = [
    projectData.similar_properties,
    projectData.similar_projects,
    projectData.more_projects_from_builder,
    projectData.project?.similar_properties,
    projectData.project?.similar_projects,
    projectData.project?.more_projects_from_builder,
  ];

  const slugs = [];
  rawLists.forEach((raw) => {
    const list = Array.isArray(raw) ? raw : raw?.projects;
    if (!Array.isArray(list)) return;
    list.forEach((item) => {
      if (isPremiumFromApi(item)) slugs.push(item?.slug || item?.project_slug || item?.id);
    });
  });
  if (slugs.length) syncPremiumProjectSlugs(slugs);
}

/**
 * Premium PDP check including listing context (remainingData) when detail API omits is_premium.
 */
export function resolvePdpPremiumStatus(projectData, options = {}) {
  const { pathname = '' } = options;

  if (pathname) {
    return isPremiumRouteMode(getPdpModeFromPathname(pathname));
  }

  return isPremiumFromApi(projectData);
}

/**
 * Full premium PDP experience: pathname mode is the source of truth on PDP routes.
 */
export function isPremiumPdpExperience(data, options = {}) {
  const { pathname = '' } = options;
  if (pathname) {
    return isPremiumRouteMode(getPdpModeFromPathname(pathname));
  }
  return isPremiumFromApi(data);
}

/** @deprecated Prefer isPremiumPdpExperience — kept for simple API/state checks. */
export function isPremiumProject(data, routeState = null, searchParams = null, ctx = {}) {
  return isPremiumPdpExperience(data, {
    routeState,
    searchParams,
    pathname: ctx.pathname || '',
    breadcrumbPremium: ctx.breadcrumbPremium || false,
    slug: ctx.slug || '',
  });
}

/**
 * @deprecated Route-based PDP uses pdpMode; kept for non-PDP callers without pathname.
 */
export function shouldBlockCategoryListingContinuation(projectData, searchParams) {
  if (premiumFromSearchParams(searchParams)) return true;
  return isPremiumFromApi(projectData);
}

/** MoreProperties / ExploreProjects belong on allprojects PDP only when user came from location search listing. */
export function isLocationSearchAllProjectsFlow(remainingData) {
  return Boolean(remainingData?.projectSections || remainingData?.safeProps);
}

/** Append premium route hint — legacy; prefer /premiumprojects paths. */
export function withPremiumQuery(url) {
  if (!url || url === '#') return url;
  if (/\/premium(?:projects|villas|plots)\//.test(url)) return url;
  if (/\/(?:projects|villa-projects|plot-projects)\//.test(url)) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}premium=1`;
}

/** Cache premium slugs from search-screen / location page API payload. */
export function syncPremiumProjectSlugsFromSearchPayload(payload) {
  if (!payload || typeof payload !== 'object') return;
  const premium =
    payload.locationpremiumbanner ??
    payload.locationPremiumBanner ??
    payload.location_premium_banner ??
    [];
  if (!Array.isArray(premium) || premium.length === 0) return;
  syncPremiumProjectSlugs(
    premium.map((i) => i?.slug || i?.project_slug || i?.id),
  );
}

const CATALOG_LIST_KEYS = [
  'bestdeals',
  'bestDeals',
  'editorschoice',
  'editorsChoice',
  'editors_choice',
  'selectproperties',
  'selectProperties',
  'newlaunches',
  'newLaunches',
  'lifestyleproperties',
  'lifestyleProperties',
  'readytomoveinprojects',
  'propertiesinlimelight',
  'sponsored',
  'locationpremiumbanner',
  'locationPremiumBanner',
  'location_premium_banner',
  'locationeditorschoice',
  'locationEditorsChoice',
  'location_editors_choice',
  'categorypremiumbanner',
  'categoryPremiumBanner',
  'category_premium_banner',
];

function forEachCatalogItem(payload, visit) {
  if (!payload || typeof payload !== 'object') return;

  CATALOG_LIST_KEYS.forEach((key) => {
    const list = payload[key];
    if (Array.isArray(list)) list.forEach(visit);
  });

  Object.keys(payload).forEach((key) => {
    if (/^allprojects/i.test(key) && Array.isArray(payload[key])) {
      payload[key].forEach(visit);
    }
  });
}

/** Category browse banner slots — premium by placement, not only is_premium flag. */
export function syncCategoryPremiumBannerSlugs(payload) {
  if (!payload || typeof payload !== 'object') return;
  const lists = [
    payload.categorypremiumbanner,
    payload.categoryPremiumBanner,
    payload.category_premium_banner,
  ];
  lists.forEach((list) => {
    if (!Array.isArray(list) || list.length === 0) return;
    syncPremiumProjectSlugs(list.map((i) => i?.slug || i?.project_slug || i?.id));
  });
}

/** Home / search payloads mark premium via is_premium even when project detail API omits it. */
export function syncPremiumSlugsFromCatalogPayload(payload) {
  if (!payload || typeof payload !== 'object') return;

  syncPremiumProjectSlugsFromSearchPayload(payload);
  syncCategoryPremiumBannerSlugs(payload);

  const slugs = [];
  forEachCatalogItem(payload, (item) => {
    if (isPremiumFromApi(item)) slugs.push(item?.slug || item?.project_slug || item?.id);
  });
  if (slugs.length) syncPremiumProjectSlugs(slugs);
}

export function isPremiumInCatalogPayload(payload, slug) {
  const slugNorm = normalizeSlug(slug);
  if (!slugNorm || !payload) return false;

  let premium = false;
  forEachCatalogItem(payload, (item) => {
    if (premium) return;
    const itemSlug = normalizeSlug(item?.slug || item?.project_slug || item?.id);
    if (itemSlug === slugNorm && isPremiumFromApi(item)) premium = true;
  });

  if (premium) return true;

  const premiumLists = [
    payload.locationpremiumbanner,
    payload.locationPremiumBanner,
    payload.location_premium_banner,
    payload.categorypremiumbanner,
    payload.categoryPremiumBanner,
    payload.category_premium_banner,
  ];
  return premiumLists.some(
    (list) =>
      Array.isArray(list) &&
      list.some(
        (item) => normalizeSlug(item?.slug || item?.project_slug || item?.id) === slugNorm,
      ),
  );
}

const HOME_CATALOG_PATHS = {
  apartment: ['home-screen', 'home'],
  villa: ['villa-buy-home-screen', 'home'],
  plot: ['plot-buy-home-screen', 'home'],
};

const HOME_CATALOG_TYPE_PARAM = {
  apartment: 'apartment_buy',
  villa: 'villa_buy',
  plot: 'plot_buy',
};

/**
 * Resolve premium from home catalog when listing/detail omit is_premium (e.g. bestdeals is_premium:true).
 */
export async function resolvePremiumFromHomeCatalog(slug, propertyType = 'apartment', fetchData) {
  const slugNorm = normalizeSlug(slug);
  if (!slugNorm || typeof fetchData !== 'function') return false;
  if (isKnownPremiumSlug(slugNorm)) return true;

  const paths = HOME_CATALOG_PATHS[propertyType] || HOME_CATALOG_PATHS.apartment;
  const typeParam = HOME_CATALOG_TYPE_PARAM[propertyType] || HOME_CATALOG_TYPE_PARAM.apartment;

  for (const path of paths) {
    try {
      const res = await fetchData(path, { type: typeParam }, { staleWhileRevalidate: true });
      const payload = res?.success !== false && res?.data != null ? res.data : res;
      if (isPremiumInCatalogPayload(payload, slugNorm)) {
        rememberPremiumProjectSlug(slugNorm);
        syncPremiumSlugsFromCatalogPayload(payload);
        return true;
      }
    } catch {
      /* try next catalog source */
    }
  }

  return false;
}

/**
 * Category browse (`categorypremiumbanner`) — items are premium by placement even when is_premium is omitted.
 */
export async function resolvePremiumFromCategoryCatalog(
  slug,
  categorySlug,
  propertyType = 'apartment',
  fetchData,
  citySlug = 'hyderabad',
) {
  const slugNorm = normalizeSlug(slug);
  const catSlug = String(categorySlug || '').trim();
  if (!slugNorm || !catSlug || typeof fetchData !== 'function') return false;
  if (isKnownPremiumSlug(slugNorm)) return true;

  const typeParam = HOME_CATALOG_TYPE_PARAM[propertyType] || HOME_CATALOG_TYPE_PARAM.apartment;
  const city = String(citySlug || 'hyderabad').trim() || 'hyderabad';

  try {
    const params = new URLSearchParams({
      type: typeParam,
      categoryname: catSlug,
      startlimit: '0',
      endlimit: '50',
      city,
    });
    const res = await fetchData(`categoryprojects?${params.toString()}`, null, {
      staleWhileRevalidate: true,
    });
    const payload = res?.success !== false && res?.data != null ? res.data : null;
    if (!payload) return false;

    syncCategoryPremiumBannerSlugs(payload);

    if (isPremiumInCatalogPayload(payload, slugNorm)) {
      rememberPremiumProjectSlug(slugNorm);
      return true;
    }
  } catch {
    /* ignore */
  }

  return false;
}
